import React, { useState } from 'react';
import { DollarSign, Save, Wallet, ClipboardCheck, Receipt, Building2, Calendar, CalendarDays } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader, TabBar, InputField, ProgressBar } from '../components/Card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const TABS = [
  { id: 'mortgage', label: 'Mortgage Calculator', icon: DollarSign },
  { id: 'downpayment', label: 'Down Payment', icon: Wallet },
  { id: 'affordability', label: 'Affordability', icon: ClipboardCheck },
  { id: 'closing', label: 'Closing Costs', icon: Receipt },
  { id: 'ltt', label: 'Land Transfer Tax', icon: Building2 },
  { id: 'monthly', label: 'Monthly Budget', icon: Calendar },
  { id: 'firstyear', label: 'First Year', icon: CalendarDays },
];

const PIE_COLORS = ['#1a2744', '#2d8a4e', '#e8972c', '#f0ad4e'];

export default function BuyerBudget() {
  const [activeTab, setActiveTab] = useState('mortgage');
  const [saveName, setSaveName] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [downPaymentMode, setDownPaymentMode] = useState('amount');
  const app = useApp();
  const { budget, setBudget, saveScenario, showToast } = app;

  const updateBudget = (key, value) => {
    setBudget(prev => ({ ...prev, [key]: value }));
  };

  const monthlyPayment = Math.round(app.getMonthlyPayment());
  const downPayment = app.getDownPaymentAmount();
  const mortgageAmount = app.getMortgageAmount();
  const ltt = app.getLandTransferTax();
  const ftbRebate = app.getFirstTimeBuyerRebate();
  const cmhcPremium = app.getCMHCPremium();
  const insuredMortgage = app.getInsuredMortgage();
  const totalSources = app.getTotalDownPaymentSources();
  const closingCosts = app.getClosingCosts();
  const stressRate = app.getStressTestRate();
  const monthlyTax = app.getMonthlyPropertyTax();
  const monthlyInsurance = app.getMonthlyInsurance();
  const totalMonthly = app.getTotalMonthlyCost();

  const handleSave = () => {
    if (saveName.trim()) {
      saveScenario(saveName.trim());
      setSaveName('');
      setShowSave(false);
      showToast('Scenario saved!');
    }
  };

  const fhsaAmount = (budget.fhsaBalance || 0) * 1000;
  const pieData = [
    { name: 'RRSP HBP', value: budget.hbpWithdrawal || 0 },
    { name: 'FHSA', value: fhsaAmount },
    { name: 'Additional Savings', value: budget.additionalSavings || 0 },
    { name: 'Tax Refund', value: budget.taxRefund || 0 },
  ].filter(d => d.value > 0);

  return (
    <div>
      <PageHeader
        icon={<DollarSign size={22} />}
        title="Buyer Budget Planner"
        subtitle="Plan your finances for your first home purchase"
        action={
          <button onClick={() => setShowSave(true)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 'var(--radius-sm)',
            background: 'var(--primary)', color: 'white', border: 'none',
            fontWeight: 500, fontSize: 14,
          }}>
            <Save size={16} /> Save Scenario
          </button>
        }
      />

      {showSave && (
        <Card style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <InputField label="Scenario Name" value={saveName}
            onChange={e => setSaveName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder="e.g. Ottawa $550k" style={{ flex: 1, marginBottom: 0 }} />
          <button onClick={handleSave} style={{
            padding: '10px 20px', background: 'var(--primary)', color: 'white',
            borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, height: 42,
          }}>Save</button>
          <button onClick={() => setShowSave(false)} style={{
            padding: '10px 16px', background: 'var(--gray-200)',
            borderRadius: 'var(--radius-sm)', fontSize: 14, height: 42,
          }}>Cancel</button>
        </Card>
      )}

      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* MORTGAGE CALCULATOR */}
      {activeTab === 'mortgage' && (
        <div>
          <Card dark style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.7, marginBottom: 4 }}>Your Monthly Payment</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>${monthlyPayment.toLocaleString()}</div>
              <div style={{ fontSize: 12, opacity: 0.6 }}>Principal: ${mortgageAmount.toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.7, marginBottom: 4 }}>Total Monthly Costs</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>${totalMonthly.toLocaleString()}</div>
              <div style={{ fontSize: 12, opacity: 0.6 }}>Includes taxes & fees</div>
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.7, marginBottom: 4 }}>Cash to Close</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>${(downPayment + closingCosts - (ltt > 0 ? ftbRebate : 0) + ltt).toLocaleString()}</div>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '4px 10px', borderRadius: 20, display: 'inline-block', fontSize: 12, marginTop: 4 }}>
                Stress Test: {stressRate.toFixed(2)}%
              </div>
            </div>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Card>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Property Details</h3>
              <InputField label="Purchase Price (max $2,000,000)" prefix="$"
                value={budget.purchasePrice}
                onChange={e => updateBudget('purchasePrice', Math.min(Number(e.target.value) || 0, 2000000))} />
              <input type="range" min={100000} max={2000000} step={5000}
                value={Math.min(budget.purchasePrice, 2000000)}
                onChange={e => updateBudget('purchasePrice', Number(e.target.value))}
                style={{ width: '100%', marginBottom: 16, accentColor: 'var(--green)' }} />

              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Down Payment</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button
                  onClick={() => setDownPaymentMode('amount')}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)',
                    background: downPaymentMode === 'amount' ? 'var(--primary)' : 'var(--gray-100)',
                    color: downPaymentMode === 'amount' ? 'white' : 'var(--gray-700)',
                    fontWeight: 500, fontSize: 13,
                  }}>$ Amount</button>
                <button
                  onClick={() => setDownPaymentMode('percent')}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)',
                    background: downPaymentMode === 'percent' ? 'var(--primary)' : 'var(--gray-100)',
                    color: downPaymentMode === 'percent' ? 'white' : 'var(--gray-700)',
                    fontWeight: 500, fontSize: 13,
                  }}>% Percent</button>
              </div>
              {downPaymentMode === 'amount' ? (
                <InputField prefix="$" value={downPayment}
                  onChange={e => {
                    const val = Number(e.target.value) || 0;
                    const pct = budget.purchasePrice > 0 ? Math.round((val / budget.purchasePrice) * 100) : 0;
                    updateBudget('downPaymentPercent', Math.min(pct, 100));
                  }} />
              ) : (
                <InputField suffix="%" value={budget.downPaymentPercent}
                  onChange={e => {
                    const val = Math.min(Number(e.target.value) || 0, 100);
                    updateBudget('downPaymentPercent', val);
                  }} />
              )}
              <div style={{ fontSize: 12, color: 'var(--gray-500)', display: 'flex', justifyContent: 'space-between' }}>
                <span>{downPaymentMode === 'amount' ? `${budget.downPaymentPercent}% down` : `$${downPayment.toLocaleString()}`}</span>
                <span>Min: ${Math.round(budget.purchasePrice * 0.05).toLocaleString()}</span>
              </div>

              {cmhcPremium > 0 && (
                <div style={{ marginTop: 12, padding: 12, background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--gray-200)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--primary)' }}>CMHC Mortgage Insurance Required</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                    <span>Insurance Premium</span><span style={{ fontWeight: 600 }}>${cmhcPremium.toLocaleString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                    <span>Total Insured Mortgage</span><span style={{ fontWeight: 600 }}>${insuredMortgage.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <InputField label="Interest Rate" suffix="%"
                  value={budget.interestRate}
                  onChange={e => updateBudget('interestRate', Number(e.target.value) || 0)} />
              </div>
              <div>
                <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, display: 'block' }}>Amortization</label>
                <select value={budget.amortization}
                  onChange={e => updateBudget('amortization', Number(e.target.value))}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 15, background: 'white' }}>
                  {[15, 20, 25, 30].map(y => <option key={y} value={y}>{y} years</option>)}
                </select>
              </div>
            </Card>

            <div>
              <Card style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><Building2 size={18} color="var(--primary)" /> Ontario Land Transfer Tax</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)' }}>
                  <span>Land transfer tax</span><span style={{ fontWeight: 600 }}>${ltt.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-200)', color: 'var(--green)' }}>
                  <span>First-time buyer rebate</span><span style={{ fontWeight: 600 }}>-${ftbRebate.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 700 }}>
                  <span>Net LTT</span><span>${(ltt - ftbRebate).toLocaleString()}</span>
                </div>
              </Card>

              <Card>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><Wallet size={18} color="var(--primary)" /> Cash Needed to Close</h3>
                {[
                  { label: 'Down payment', value: downPayment },
                  { label: 'Land transfer tax (net)', value: ltt - ftbRebate },
                  { label: 'Legal fees (est.)', value: 2000 },
                  { label: 'Home inspection', value: 500 },
                  { label: 'Title insurance', value: 400 },
                  { label: 'Other closing costs', value: 1000 },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 14 }}>
                    <span>{item.label}</span><span style={{ fontWeight: 600 }}>${item.value.toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700, fontSize: 16, borderTop: '2px solid var(--gray-300)', marginTop: 8 }}>
                  <span>Total</span><span>${(downPayment + (ltt - ftbRebate) + 3900).toLocaleString()}</span>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* DOWN PAYMENT */}
      {activeTab === 'downpayment' && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Purchase Target</h3>
            <InputField label="Target Purchase Price" prefix="$"
              value={budget.purchasePrice}
              onChange={e => updateBudget('purchasePrice', Number(e.target.value) || 0)} />

            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                <span>Down Payment: {budget.downPaymentPercent}%</span>
              </div>
              <input type="range" min={5} max={50} step={1}
                value={budget.downPaymentPercent}
                onChange={e => updateBudget('downPaymentPercent', Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--green)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-500)' }}>
                <span>5% (CMHC insured)</span>
                <span>20%+ (no insurance)</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 500, marginTop: 12 }}>
              <span>Required Down Payment:</span>
              <span style={{ fontWeight: 700, fontSize: 18 }}>${downPayment.toLocaleString()}</span>
            </div>
          </Card>

          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>First Home Savings Account (FHSA)</h3>
            <InputField label="Current FHSA Balance" prefix="$"
              value={budget.fhsaBalance}
              onChange={e => updateBudget('fhsaBalance', Number(e.target.value) || 0)} />
            <InputField label="Annual FHSA Contribution (max $8,000)" prefix="$"
              value={budget.fhsaContribution}
              onChange={e => updateBudget('fhsaContribution', Number(e.target.value) || 0)} />
          </Card>

          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>RRSP Home Buyers' Plan (HBP)</h3>
            <InputField label="Current RRSP Balance" prefix="$"
              value={budget.rrspHBP}
              onChange={e => updateBudget('rrspHBP', Number(e.target.value) || 0)} />
            <InputField label="HBP Withdrawal Amount (max $60,000)" prefix="$"
              value={budget.hbpWithdrawal}
              onChange={e => updateBudget('hbpWithdrawal', Math.min(Number(e.target.value) || 0, 60000))} />
            <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
              Annual repayment: ${Math.round(budget.hbpWithdrawal / 15).toLocaleString()}/year for 15 years
            </div>
          </Card>

          <Card style={{ marginBottom: 20 }}>
            <InputField label="Additional Savings" prefix="$"
              value={budget.additionalSavings}
              onChange={e => updateBudget('additionalSavings', Number(e.target.value) || 0)} />
          </Card>

          <Card>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Down Payment Sources</h3>
            {pieData.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={110} innerRadius={60} paddingAngle={2}
                      label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}
                    >
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => `$${v.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: 'var(--gray-600)' }}>Total Available</div>
              <div style={{ fontSize: 36, fontWeight: 700, color: 'var(--primary)' }}>${totalSources.toLocaleString()}</div>
            </div>
          </Card>
        </div>
      )}

      {/* AFFORDABILITY */}
      {activeTab === 'affordability' && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Affordability Calculator</h3>
            <InputField label="Annual Household Income" prefix="$"
              value={budget.annualIncome}
              onChange={e => updateBudget('annualIncome', Number(e.target.value) || 0)} />
            <InputField label="Monthly Debt Payments" prefix="$"
              value={budget.monthlyDebt}
              onChange={e => updateBudget('monthlyDebt', Number(e.target.value) || 0)} />
            <InputField label="Property Tax Rate" suffix="%"
              value={1.1} onChange={() => {}} />
            <InputField label="Heating Costs (monthly)" prefix="$"
              value={150} onChange={() => {}} />
          </Card>
          <Card dark style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 8 }}>Maximum Affordable Purchase Price</div>
            <div style={{ fontSize: 42, fontWeight: 700 }}>${Math.round((budget.annualIncome || 0) * 4.5).toLocaleString()}</div>
            <div style={{ fontSize: 13, opacity: 0.6, marginTop: 8 }}>
              Based on stress test rate of {stressRate.toFixed(2)}% with GDS/TDS ratios
            </div>
            {(() => {
              const monthlyIncome = (budget.annualIncome || 0) / 12;
              const housingCost = monthlyPayment + monthlyTax + 150;
              const gds = monthlyIncome > 0 ? Math.round((housingCost / monthlyIncome) * 1000) / 10 : 0;
              const tds = monthlyIncome > 0 ? Math.round(((housingCost + (budget.monthlyDebt || 0)) / monthlyIncome) * 1000) / 10 : 0;
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
                  <div style={{ background: 'rgba(255,255,255,0.1)', padding: 16, borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>GDS Ratio</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: gds > 39 ? 'var(--red)' : 'inherit' }}>{gds}%</div>
                    <div style={{ fontSize: 11, opacity: 0.5 }}>Max 39%</div>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.1)', padding: 16, borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>TDS Ratio</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: tds > 44 ? 'var(--red)' : 'inherit' }}>{tds}%</div>
                    <div style={{ fontSize: 11, opacity: 0.5 }}>Max 44%</div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      {/* CLOSING COSTS */}
      {activeTab === 'closing' && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Estimated Closing Costs</h3>
            <p style={{ color: 'var(--gray-600)', fontSize: 14, marginBottom: 20 }}>
              Budget approximately 1.5-4% of purchase price for closing costs.
            </p>
            {[
              { label: 'Legal Fees & Disbursements', est: '$1,500 - $2,500', value: 2000 },
              { label: 'Title Insurance', est: '$250 - $500', value: 400 },
              { label: 'Land Transfer Tax (Ontario, net)', est: 'Varies', value: ltt - ftbRebate },
              { label: 'Home Inspection', est: '$400 - $600', value: 500 },
              { label: 'Appraisal Fee (if required)', est: '$300 - $500', value: 0 },
              { label: 'Property Survey (if required)', est: '$1,000 - $2,000', value: 0 },
              { label: 'Moving Costs', est: '$800 - $2,000', value: 1500 },
              { label: 'Utility Hookup Fees', est: '$100 - $300', value: 200 },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0', borderBottom: '1px solid var(--gray-100)',
              }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{item.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{item.est}</div>
                </div>
                <span style={{ fontWeight: 600 }}>${item.value.toLocaleString()}</span>
              </div>
            ))}
            <div style={{
              display: 'flex', justifyContent: 'space-between', padding: '16px 0',
              fontWeight: 700, fontSize: 18, borderTop: '2px solid var(--gray-300)', marginTop: 8,
            }}>
              <span>Estimated Total</span>
              <span>${(2000 + 400 + (ltt - ftbRebate) + 500 + 1500 + 200).toLocaleString()}</span>
            </div>
          </Card>
        </div>
      )}

      {/* LAND TRANSFER TAX */}
      {activeTab === 'ltt' && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Ontario Land Transfer Tax Calculator</h3>
            <InputField label="Purchase Price" prefix="$"
              value={budget.purchasePrice}
              onChange={e => updateBudget('purchasePrice', Number(e.target.value) || 0)} />
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={budget.isFirstTimeBuyer} onChange={e => updateBudget('isFirstTimeBuyer', e.target.checked)} style={{ width: 18, height: 18 }} />
                First-time home buyer
              </label>
            </div>
          </Card>
          <Card>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Tax Breakdown</h3>
            <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 16 }}>
              Ontario LTT is calculated on a marginal rate system.
            </div>
            {[
              { bracket: 'First $55,000', rate: '0.5%', tax: Math.min(budget.purchasePrice, 55000) * 0.005 },
              { bracket: '$55,001 - $250,000', rate: '1.0%', tax: Math.max(0, Math.min(budget.purchasePrice, 250000) - 55000) * 0.01 },
              { bracket: '$250,001 - $400,000', rate: '1.5%', tax: Math.max(0, Math.min(budget.purchasePrice, 400000) - 250000) * 0.015 },
              { bracket: '$400,001 - $2,000,000', rate: '2.0%', tax: Math.max(0, Math.min(budget.purchasePrice, 2000000) - 400000) * 0.02 },
            ].map((b, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 14 }}>
                <span>{b.bracket} @ {b.rate}</span>
                <span style={{ fontWeight: 500 }}>${Math.round(b.tax).toLocaleString()}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 600, borderTop: '2px solid var(--gray-300)', marginTop: 8 }}>
              <span>Total LTT</span><span>${ltt.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: 'var(--green)' }}>
              <span>First-time buyer rebate</span><span>-${ftbRebate.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700, fontSize: 18, background: 'var(--green-light)', margin: '8px -24px -24px', padding: '16px 24px', borderRadius: '0 0 var(--radius) var(--radius)' }}>
              <span>Net LTT Payable</span><span>${(ltt - ftbRebate).toLocaleString()}</span>
            </div>
          </Card>
        </div>
      )}

      {/* MONTHLY BUDGET */}
      {activeTab === 'monthly' && (
        <div>
          <Card dark style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 4 }}>Total Monthly Housing Cost</div>
            <div style={{ fontSize: 42, fontWeight: 700 }}>${totalMonthly.toLocaleString()}</div>
          </Card>
          <Card>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Monthly Breakdown</h3>
            {[
              { label: 'Mortgage Payment', value: monthlyPayment, color: 'var(--primary)' },
              { label: 'Property Tax', value: monthlyTax, color: 'var(--primary)' },
              { label: 'Home Insurance', value: monthlyInsurance, color: 'var(--primary)' },
              { label: 'Utilities (est.)', value: 300, color: 'var(--primary)' },
              { label: 'Maintenance (1% rule / 12)', value: Math.round(budget.purchasePrice * 0.01 / 12), color: 'var(--primary)' },
            ].map((item, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14 }}>
                  <span style={{ fontWeight: 500 }}>{item.label}</span>
                  <span style={{ fontWeight: 600 }}>${item.value.toLocaleString()}/mo</span>
                </div>
                <ProgressBar value={item.value} max={monthlyPayment * 1.2} color={item.color} height={6} />
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* FIRST YEAR */}
      {activeTab === 'firstyear' && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>First Year Cost Estimate</h3>
            <p style={{ color: 'var(--gray-600)', fontSize: 14, marginBottom: 20 }}>
              Your first year of homeownership will include both one-time and recurring costs.
            </p>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 12 }}>ONE-TIME COSTS</h4>
            {[
              { label: 'Down Payment', value: downPayment },
              { label: 'Closing Costs (est.)', value: closingCosts },
              { label: 'Moving Costs', value: 1500 },
              { label: 'Immediate Repairs/Updates', value: 3000 },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 14 }}>
                <span>{item.label}</span><span style={{ fontWeight: 600 }}>${item.value.toLocaleString()}</span>
              </div>
            ))}

            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', margin: '20px 0 12px' }}>ANNUAL RECURRING</h4>
            {[
              { label: 'Mortgage Payments', value: monthlyPayment * 12 },
              { label: 'Property Tax', value: monthlyTax * 12 },
              { label: 'Home Insurance', value: monthlyInsurance * 12 },
              { label: 'Utilities', value: 3600 },
              { label: 'Maintenance Reserve', value: Math.round(budget.purchasePrice * 0.01) },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 14 }}>
                <span>{item.label}</span><span style={{ fontWeight: 600 }}>${item.value.toLocaleString()}</span>
              </div>
            ))}

            <div style={{
              display: 'flex', justifyContent: 'space-between', padding: '16px 0', marginTop: 8,
              fontWeight: 700, fontSize: 18, borderTop: '2px solid var(--gray-300)',
            }}>
              <span>Total First Year</span>
              <span>${(downPayment + closingCosts + 4500 + (monthlyPayment * 12) + (monthlyTax * 12) + (monthlyInsurance * 12) + 3600 + Math.round(budget.purchasePrice * 0.01)).toLocaleString()}</span>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
